import React, { useState, useRef, useEffect } from "react";
import {
  View,
  TextInput,
  StyleSheet,
  TouchableOpacity,
  Keyboard,
  Text,
  Animated,
  Platform,
  Pressable,
  Modal,
  ScrollView,
  Dimensions,
  ActivityIndicator,
} from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { LinearGradient } from "expo-linear-gradient";
import { useAuth } from "../contexts/AuthContext";
import { useTheme } from "../contexts/ThemeContext";
import { useNavigation } from "@react-navigation/native";
import AuthRequiredModal from "./auth/AuthRequiredModal";

interface SearchBarProps {
  value: string;
  onChangeText: (text: string) => void;
  onSubmit?: () => void;
  placeholder?: string;
  isAISearchEnabled?: boolean;
  onAISearchToggle?: (enabled: boolean) => void;
  onFilterPress?: () => void;
  isFilterActive?: boolean;
}

const { width } = Dimensions.get("window");
// Responsive breakpoints for real phone sizes
const isMobile = width < 500; // General mobile detection
const isLargeMobile = width < 450 && width >= 390; // iPhone 14 Pro Max, etc.
const isMediumMobile = width < 390 && width >= 375; // iPhone 12 Pro, XR, etc. 
const isSmallMobile = width < 375 && width >= 350; // iPhone SE, etc.
const isTinyMobile = width < 350; // Very small phones

const SearchBar: React.FC<SearchBarProps> = ({
  value,
  onChangeText,
  onSubmit,
  placeholder = "Search opportunities...",
  isAISearchEnabled = false,
  onAISearchToggle,
  onFilterPress,
  isFilterActive = false,
}) => {
  const { isAuthenticated } = useAuth();
  const { theme } = useTheme();
  const navigation = useNavigation<any>();
  const [showInfoPopup, setShowInfoPopup] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  
  // Animation values
  const aiActivationAnim = useRef(new Animated.Value(isAISearchEnabled ? 1 : 0)).current;
  const infoPopupOpacity = useRef(new Animated.Value(0)).current;
  const infoPopupScale = useRef(new Animated.Value(0.9)).current;
  const gradientAnim = useRef(new Animated.Value(isAISearchEnabled ? 1 : 0)).current;
  const shimmerAnim = useRef(new Animated.Value(-1)).current;

  // Professional color scheme - refined to match site design
  const colors = {
    light: {
      background: "#FAFAFA",
      searchBg: "#FFFFFF",
      searchBorder: "#E4E4E7",
      searchFocusBorder: "#A1A1AA",
      text: "#18181B",
      placeholder: "#71717A",
      icon: "#71717A",
      iconActive: "#18181B",
      aiToggleBg: "#F4F4F5",
      aiToggleActiveBg: "#18181B",
      aiToggleIcon: "#52525B",
      aiToggleActiveIcon: "#FFFFFF",
      filterBg: "#FFFFFF",
      filterActiveBg: "#18181B",
      filterIcon: "#71717A",
      filterActiveIcon: "#FFFFFF",
      infoBg: "#FFFFFF",
      infoText: "#18181B",
      infoSubtext: "#71717A",
      shadowColor: "#000000",
      // Refined gradient colors - more subtle and professional
      gradientStart: "#4F46E5", // Indigo
      gradientMid: "#7C3AED", // Purple
      gradientEnd: "#EC4899", // Pink
      divider: "#E4E4E7",
      modalBg: "rgba(0, 0, 0, 0.5)",
      modalOverlay: "rgba(0, 0, 0, 0.5)",
      cardBg: "#FFFFFF",
      exampleBg: "#F9FAFB",
      benefitBg: "#F9FAFB",
      accentColor: "#4F46E5",
    },
    dark: {
      background: "#18181B",
      searchBg: "#27272A",
      searchBorder: "#3F3F46",
      searchFocusBorder: "#71717A",
      text: "#FAFAFA",
      placeholder: "#A1A1AA",
      icon: "#A1A1AA",
      iconActive: "#FAFAFA",
      aiToggleBg: "#3F3F46",
      aiToggleActiveBg: "#FAFAFA",
      aiToggleIcon: "#A1A1AA",
      aiToggleActiveIcon: "#18181B",
      filterBg: "#27272A",
      filterActiveBg: "#FAFAFA",
      filterIcon: "#A1A1AA",
      filterActiveIcon: "#18181B",
      infoBg: "#27272A",
      infoText: "#FAFAFA",
      infoSubtext: "#A1A1AA",
      shadowColor: "#000000",
      // Refined gradient colors for dark theme
      gradientStart: "#6366F1", // Lighter Indigo
      gradientMid: "#8B5CF6", // Lighter Purple
      gradientEnd: "#EC4899", // Pink remains vibrant
      divider: "#3F3F46",
      modalBg: "rgba(0, 0, 0, 0.7)",
      modalOverlay: "rgba(0, 0, 0, 0.8)",
      cardBg: "#18181B",
      exampleBg: "#3F3F46",
      benefitBg: "#3F3F46",
      accentColor: "#6366F1",
    },
  };

  const c = colors[theme];

  // AI activation animation with gradient and shimmer effects
  useEffect(() => {
    if (isAISearchEnabled) {
      // Start all animations when AI is enabled
      Animated.parallel([
        Animated.timing(aiActivationAnim, {
          toValue: 1,
          duration: 400,
          useNativeDriver: false,
        }),
        Animated.timing(gradientAnim, {
          toValue: 1,
          duration: 500,
          useNativeDriver: false,
        }),
      ]).start();

      // Start shimmer effect
      const shimmerAnimation = Animated.loop(
        Animated.timing(shimmerAnim, {
          toValue: 1,
          duration: 3000,
          useNativeDriver: false,
        })
      );
      shimmerAnimation.start();

      return () => {
        shimmerAnimation.stop();
      };
    } else {
      // Disable animations when AI is off
      Animated.parallel([
        Animated.timing(aiActivationAnim, {
          toValue: 0,
          duration: 300,
          useNativeDriver: false,
        }),
        Animated.timing(gradientAnim, {
          toValue: 0,
          duration: 300,
          useNativeDriver: false,
        }),
      ]).start();
    }
  }, [isAISearchEnabled]);

  const handleClear = () => {
    onChangeText("");
  };

  const handleSubmit = () => {
    Keyboard.dismiss();
    if (onSubmit) {
      setIsSearching(true);
      onSubmit();
      setTimeout(() => setIsSearching(false), 1000);
    }
  };

  const handleAIToggle = () => {
    if (!isAuthenticated) {
      setShowAuthModal(true);
      return;
    }

    if (onAISearchToggle) {
      onAISearchToggle(!isAISearchEnabled);
    }
  };

  const showInfo = () => {
    setShowInfoPopup(true);
    Animated.parallel([
      Animated.timing(infoPopupOpacity, {
        toValue: 1,
        duration: 300,
        useNativeDriver: true,
      }),
      Animated.spring(infoPopupScale, {
        toValue: 1,
        tension: 50,
        friction: 7,
        useNativeDriver: true,
      }),
    ]).start();
  };

  const hideInfo = () => {
    Animated.parallel([
      Animated.timing(infoPopupOpacity, {
        toValue: 0,
        duration: 200,
        useNativeDriver: true,
      }),
      Animated.timing(infoPopupScale, {
        toValue: 0.9,
        duration: 200,
        useNativeDriver: true,
      }),
    ]).start(() => {
      setShowInfoPopup(false);
    });
  };

  const handleSignUpPress = () => {
    hideInfo();
    navigation.navigate("SignUp");
  };

  // Animated shimmer position
  const shimmerTranslateX = shimmerAnim.interpolate({
    inputRange: [-1, 1],
    outputRange: [-300, 300],
  });

  return (
    <View style={styles.container}>
      <View style={styles.searchRow}>
        <View style={[
          styles.searchContainer,
          {
            borderWidth: 1,
            borderColor: isAISearchEnabled 
              ? "transparent"
              : (isFocused ? c.searchFocusBorder : c.searchBorder),
          }
        ]}>
          {/* Subtle AI Gradient Overlay */}
          {isAISearchEnabled && (
            <Animated.View
              style={[
                StyleSheet.absoluteFillObject,
                styles.gradientOverlay,
                {
                  opacity: gradientAnim.interpolate({
                    inputRange: [0, 1],
                    outputRange: [0, 0.6], // Reduced opacity for subtlety
                  }),
                },
              ]}
              pointerEvents="none"
            >
              <LinearGradient
                colors={[
                  `${c.gradientStart}40`, // Much more subtle with alpha
                  `${c.gradientMid}30`,
                  `${c.gradientEnd}40`
                ]}
                style={StyleSheet.absoluteFillObject}
                start={{ x: 0, y: 0 }}
                end={{ x: 1, y: 0 }}
                locations={[0, 0.5, 1]}
              />
              
              {/* Very subtle shimmer effect */}
              <Animated.View
                style={[
                  styles.shimmerEffect,
                  {
                    transform: [{ translateX: shimmerTranslateX }],
                  },
                ]}
              >
                <LinearGradient
                  colors={['transparent', 'rgba(255,255,255,0.03)', 'transparent']}
                  style={styles.shimmerGradient}
                  start={{ x: 0, y: 0 }}
                  end={{ x: 1, y: 0 }}
                />
              </Animated.View>
            </Animated.View>
          )}

          {/* Regular background when AI is off */}
          {!isAISearchEnabled && (
            <View
              style={[
                StyleSheet.absoluteFillObject,
                styles.regularBackground,
                {
                  backgroundColor: c.searchBg,
                },
              ]}
            />
          )}

          <View style={styles.searchContent}>
            <View style={styles.iconContainer}>
              <Animated.View 
                style={[
                  styles.iconWrapper,
                  { opacity: aiActivationAnim }
                ]}
              >
                <Ionicons
                  name="sparkles"
                  size={20}
                  color={isAISearchEnabled 
                    ? (theme === 'light' ? c.accentColor : "#6366F1")
                    : c.accentColor}
                />
              </Animated.View>

              <Animated.View 
                style={[
                  styles.iconWrapper,
                  { 
                    opacity: aiActivationAnim.interpolate({
                      inputRange: [0, 1],
                      outputRange: [1, 0],
                    })
                  }
                ]}
              >
                <Ionicons
                  name="search"
                  size={20}
                  color={isFocused ? c.iconActive : c.icon}
                />
              </Animated.View>
            </View>

            <TextInput
              style={[
                styles.input, 
                { 
                  color: isAISearchEnabled 
                    ? (theme === 'light' ? c.text : c.text) 
                    : c.text,
                  minWidth: 0, // Allow input to shrink as needed
                }
              ]}
              value={value}
              onChangeText={onChangeText}
              onSubmitEditing={handleSubmit}
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
              placeholder={
                isAISearchEnabled
                  ? (isTinyMobile ? "Ask..." : isSmallMobile || isMediumMobile ? "Ask anything..." : "Ask anything...")
                  : (isTinyMobile ? "Search..." : isSmallMobile ? "Find opportunities..." : placeholder)
              }
              placeholderTextColor={isAISearchEnabled 
                ? (theme === 'light' ? c.placeholder : c.placeholder)
                : c.placeholder}
              returnKeyType="search"
              clearButtonMode="never"
              numberOfLines={1}
            />

            {/* Right side buttons container */}
            <View style={styles.rightButtonsContainer}>
              {isSearching && (
                <ActivityIndicator
                  size="small"
                  color={isAISearchEnabled 
                    ? (theme === 'light' ? c.accentColor : "#6366F1")
                    : c.iconActive}
                  style={styles.loader}
                />
              )}

              {!isSearching && value.length > 0 && (
                <TouchableOpacity style={styles.clearButton} onPress={handleClear}>
                  <Ionicons 
                    name="close-circle" 
                    size={18} 
                    color={isAISearchEnabled 
                      ? (theme === 'light' ? c.icon : c.icon)
                      : c.icon} 
                  />
                </TouchableOpacity>
              )}

              {/* Enter key indicator - only show when AI search is active */}
              {!isSearching && value.length > 0 && isAISearchEnabled && (
                <TouchableOpacity style={styles.enterButton} onPress={handleSubmit}>
                  <View style={[
                    styles.enterKeyIndicator,
                    {
                      backgroundColor: theme === 'light' ? "rgba(79, 70, 229, 0.1)" : "rgba(99, 102, 241, 0.15)",
                      borderColor: theme === 'light' ? "rgba(79, 70, 229, 0.2)" : "rgba(99, 102, 241, 0.3)",
                    }
                  ]}>
                    <Ionicons 
                      name="return-down-back-outline" 
                      size={14} 
                      color={theme === 'light' ? c.accentColor : "#6366F1"}
                    />
                  </View>
                </TouchableOpacity>
              )}

              {/* Divider */}
              {!isSearching && (
                <View style={[
                  styles.divider, 
                  { backgroundColor: isAISearchEnabled 
                      ? (theme === 'light' ? "rgba(79, 70, 229, 0.15)" : "rgba(99, 102, 241, 0.2)")
                      : c.divider }
                ]} />
              )}

              {/* Enhanced AI Toggle Switch */}
              <View style={styles.aiToggleContainer}>
                <TouchableOpacity
                  onPress={handleAIToggle}
                  onLongPress={showInfo}
                  style={[
                    styles.aiToggleWrapper,
                    {
                      backgroundColor: isAISearchEnabled 
                        ? (theme === 'light' ? "rgba(79, 70, 229, 0.08)" : "rgba(99, 102, 241, 0.12)")
                        : (theme === 'light' ? "#F8F9FA" : "#2A2A2A"),
                      borderWidth: 1,
                      borderColor: isAISearchEnabled 
                        ? (theme === 'light' ? "rgba(79, 70, 229, 0.2)" : "rgba(99, 102, 241, 0.3)")
                        : (theme === 'light' ? "#E5E7EB" : "#404040"),
                    }
                  ]}
                  activeOpacity={0.7}
                >
                  {!isSmallMobile && !isTinyMobile && (
                    <Ionicons
                      name="sparkles"
                      size={isTinyMobile ? 10 : isSmallMobile ? 11 : 12}
                      color={isAISearchEnabled 
                        ? (theme === 'light' ? c.accentColor : "#6366F1")
                        : c.placeholder}
                      style={styles.toggleIcon}
                    />
                  )}
                  
                  {/* Toggle Switch Track */}
                  <View style={[
                    styles.toggleTrack,
                    {
                      backgroundColor: isAISearchEnabled 
                        ? (theme === 'light' ? "rgba(79, 70, 229, 0.15)" : "rgba(99, 102, 241, 0.2)")
                        : (theme === 'light' ? "#E4E4E7" : "#3F3F46"),
                    }
                  ]}>
                    <Animated.View style={[
                      styles.toggleThumb,
                      {
                        backgroundColor: isAISearchEnabled 
                          ? (theme === 'light' ? c.accentColor : "#6366F1")
                          : (theme === 'light' ? "#71717A" : "#A1A1AA"),
                        transform: [{
                          translateX: aiActivationAnim.interpolate({
                            inputRange: [0, 1],
                            outputRange: [1, isTinyMobile ? 8 : isSmallMobile || isMediumMobile ? 10 : isLargeMobile ? 12 : 14],
                          })
                        }]
                      }
                    ]} />
                  </View>

                  {/* Info indicator - hide on tiny mobile */}
                  {!isTinyMobile && (
                    <TouchableOpacity 
                      onPress={showInfo}
                      style={styles.subtleInfoButton}
                      activeOpacity={0.6}
                    >
                      <Ionicons 
                        name="information-circle-outline" 
                        size={isTinyMobile ? 10 : isSmallMobile ? 11 : 12} 
                        color={c.placeholder} 
                      />
                    </TouchableOpacity>
                  )}
                </TouchableOpacity>
              </View>
            </View>
          </View>
        </View>

        {/* Filter button */}
        {onFilterPress && (
          <TouchableOpacity
            style={[
              styles.filterButton,
              {
                backgroundColor: isFilterActive ? c.filterActiveBg : c.filterBg,
                borderWidth: 1,
                borderColor: isFilterActive ? c.filterActiveBg : c.searchBorder,
              },
            ]}
            onPress={onFilterPress}
            activeOpacity={0.7}
          >
            <Ionicons
              name="options-outline"
              size={20}
              color={isFilterActive ? c.filterActiveIcon : c.filterIcon}
            />
          </TouchableOpacity>
        )}
      </View>

      {/* Refined AI Search Info Modal */}
      <Modal
        visible={showInfoPopup}
        transparent
        animationType="none"
        onRequestClose={hideInfo}
      >
        <View style={[styles.modalOverlay, { backgroundColor: c.modalOverlay }]}>
          <Pressable style={styles.modalBackdrop} onPress={hideInfo} />
          
          <Animated.View
            style={[
              styles.infoModal,
              {
                opacity: infoPopupOpacity,
                transform: [{ scale: infoPopupScale }],
                backgroundColor: c.cardBg,
                borderWidth: 1,
                borderColor: c.searchBorder,
                shadowColor: c.shadowColor,
              },
            ]}
          >
            {/* Clean Modal Header */}
            <View style={[styles.modalHeader, { borderBottomColor: c.divider }]}>
              <View style={styles.modalHeaderContent}>
                <LinearGradient
                  colors={[c.gradientStart, c.gradientEnd]}
                  style={styles.modalHeaderIcon}
                  start={{ x: 0, y: 0 }}
                  end={{ x: 1, y: 1 }}
                >
                  <Ionicons name="sparkles" size={20} color="#FFFFFF" />
                </LinearGradient>
                <View style={styles.modalHeaderText}>
                  <Text style={[styles.modalTitle, { color: c.infoText }]}>
                    AI-Powered Search
                  </Text>
                  <Text style={[styles.modalSubtitle, { color: c.infoSubtext }]}>
                    Find opportunities with natural language
                  </Text>
                </View>
              </View>
              <TouchableOpacity style={styles.modalCloseButton} onPress={hideInfo}>
                <Ionicons name="close" size={20} color={c.icon} />
              </TouchableOpacity>
            </View>

            {/* Clean Modal Content */}
            <ScrollView style={styles.modalContent} showsVerticalScrollIndicator={false}>
              {/* Simple Benefits */}
              <View style={styles.benefitsSection}>
                {[
                  { icon: "flash", title: "3x Faster", desc: "Find what you need instantly" },
                  { icon: "bulb", title: "Smart Context", desc: "Understands your intent" },
                  { icon: "checkmark-circle", title: "Better Matches", desc: "More relevant results" }
                ].map((benefit, index) => (
                  <View key={index} style={[styles.benefitRow, { backgroundColor: c.benefitBg }]}>
                    <View style={[styles.benefitIcon, { backgroundColor: c.accentColor + '15' }]}>
                      <Ionicons name={benefit.icon as any} size={18} color={c.accentColor} />
                    </View>
                    <View style={styles.benefitContent}>
                      <Text style={[styles.benefitTitle, { color: c.infoText }]}>
                        {benefit.title}
                      </Text>
                      <Text style={[styles.benefitDesc, { color: c.infoSubtext }]}>
                        {benefit.desc}
                      </Text>
                    </View>
                  </View>
                ))}
              </View>

              {/* Example Queries */}
              <View style={styles.examplesSection}>
                <Text style={[styles.sectionTitle, { color: c.infoText }]}>
                  Try asking questions like:
                </Text>
                {[
                  "Tech internships for computer science students",
                  "Scholarships for first-generation college students",
                  "Environmental volunteer opportunities near me"
                ].map((query, index) => (
                  <TouchableOpacity
                    key={index}
                    style={[styles.exampleButton, { 
                      backgroundColor: c.exampleBg,
                      borderWidth: 1,
                      borderColor: c.searchBorder,
                    }]}
                    onPress={() => {
                      hideInfo();
                      onChangeText(query);
                      if (onAISearchToggle && !isAISearchEnabled) {
                        if (isAuthenticated) {
                          onAISearchToggle(true);
                        } else {
                          setShowAuthModal(true);
                        }
                      }
                    }}
                    activeOpacity={0.7}
                  >
                    <Ionicons name="search" size={14} color={c.accentColor} />
                    <Text style={[styles.exampleText, { color: c.infoText }]} numberOfLines={1}>
                      {query}
                    </Text>
                  </TouchableOpacity>
                ))}
              </View>

              {/* CTA Section */}
              <View style={styles.ctaSection}>
                {!isAuthenticated ? (
                  <>
                    <TouchableOpacity
                      style={styles.primaryButton}
                      onPress={handleSignUpPress}
                      activeOpacity={0.8}
                    >
                      <LinearGradient
                        colors={[c.gradientStart, c.gradientEnd]}
                        style={styles.primaryButtonGradient}
                        start={{ x: 0, y: 0 }}
                        end={{ x: 1, y: 0 }}
                      >
                        <Text style={styles.primaryButtonText}>
                          Sign Up to Use AI Search
                        </Text>
                      </LinearGradient>
                    </TouchableOpacity>
                    <TouchableOpacity onPress={hideInfo}>
                      <Text style={[styles.secondaryButtonText, { color: c.infoSubtext }]}>
                        Continue with regular search
                      </Text>
                    </TouchableOpacity>
                  </>
                ) : (
                  <TouchableOpacity
                    style={styles.primaryButton}
                    onPress={() => {
                      hideInfo();
                      if (onAISearchToggle && !isAISearchEnabled) {
                        onAISearchToggle(true);
                      }
                    }}
                    activeOpacity={0.8}
                  >
                    <LinearGradient
                      colors={[c.gradientStart, c.gradientEnd]}
                      style={styles.primaryButtonGradient}
                      start={{ x: 0, y: 0 }}
                      end={{ x: 1, y: 0 }}
                    >
                      <Text style={styles.primaryButtonText}>
                        Enable AI Search
                      </Text>
                    </LinearGradient>
                  </TouchableOpacity>
                )}
              </View>
            </ScrollView>
          </Animated.View>
        </View>
      </Modal>

      {/* Authentication Required Modal */}
      <AuthRequiredModal
        visible={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        featureName="AI-Powered Search"
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    width: "100%",
  },
  searchRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  searchContainer: {
    flex: 1,
    position: "relative",
    height: 48,
    borderRadius: 12,
    overflow: "hidden",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 3,
    elevation: 2,
  },
  gradientOverlay: {
    borderRadius: 12,
  },
  shimmerEffect: {
    position: "absolute",
    top: 0,
    bottom: 0,
    width: 150,
    left: -75,
  },
  shimmerGradient: {
    flex: 1,
    width: "100%",
  },
  regularBackground: {
    borderRadius: 12,
  },
  searchContent: {
    flexDirection: "row",
    alignItems: "center",
    paddingLeft: isTinyMobile ? 10 : isSmallMobile || isMediumMobile ? 12 : 16,
    paddingRight: isTinyMobile ? 2 : isSmallMobile || isMediumMobile ? 4 : 8,
    height: 48,
    position: "relative",
    zIndex: 1,
  },
  iconContainer: {
    position: "relative",
    width: 20,
    height: 20,
    marginRight: isTinyMobile ? 8 : isSmallMobile || isMediumMobile ? 10 : 12,
  },
  iconWrapper: {
    position: "absolute",
    top: 0,
    left: 0,
    width: 20,
    height: 20,
    alignItems: "center",
    justifyContent: "center",
  },
  input: {
    flex: 1,
    fontSize: isTinyMobile ? 14 : isSmallMobile || isMediumMobile ? 15 : 16,
    fontFamily: "Inter-Regular",
    paddingVertical: 0,
  },
  rightButtonsContainer: {
    flexDirection: "row",
    alignItems: "center",
    flexShrink: 0,
    maxWidth: isTinyMobile ? 65 : isSmallMobile ? 80 : isMediumMobile ? 95 : isLargeMobile ? 110 : 140,
  },
  clearButton: {
    padding: isTinyMobile ? 2 : isSmallMobile || isMediumMobile ? 3 : 4,
  },
  enterButton: {
    padding: isTinyMobile ? 2 : isSmallMobile || isMediumMobile ? 3 : 4,
    marginLeft: isTinyMobile ? 1 : isSmallMobile || isMediumMobile ? 2 : 3,
  },
  enterKeyIndicator: {
    width: isTinyMobile ? 18 : isSmallMobile || isMediumMobile ? 20 : 22,
    height: isTinyMobile ? 16 : isSmallMobile || isMediumMobile ? 17 : 18,
    borderRadius: 4,
    borderWidth: 1,
    alignItems: "center",
    justifyContent: "center",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.04,
    shadowRadius: 1,
    elevation: 1,
  },
  loader: {
    marginHorizontal: isTinyMobile ? 3 : isSmallMobile || isMediumMobile ? 4 : 6,
  },
  divider: {
    width: 1,
    height: isTinyMobile ? 16 : isSmallMobile || isMediumMobile ? 18 : 22,
    marginHorizontal: isTinyMobile ? 2 : isSmallMobile || isMediumMobile ? 3 : 6,
  },
  aiToggleContainer: {
    flexDirection: "row",
    alignItems: "center",
    gap: 0,
    flexShrink: 0,
  },
  aiToggleWrapper: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: isTinyMobile ? 1 : isSmallMobile || isMediumMobile ? 2 : isLargeMobile ? 4 : 6,
    paddingVertical: isTinyMobile ? 1 : isSmallMobile || isMediumMobile ? 2 : 3,
    gap: isTinyMobile ? 0 : isSmallMobile || isMediumMobile ? 1 : 2,
    minHeight: isTinyMobile ? 20 : isSmallMobile || isMediumMobile ? 24 : 28,
    borderRadius: 6,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.03,
    shadowRadius: 2,
    elevation: 1,
  },
  toggleIcon: {
    marginRight: isTinyMobile ? 0 : isSmallMobile || isMediumMobile ? 0 : 1,
  },
  toggleTrack: {
    width: isTinyMobile ? 16 : isSmallMobile || isMediumMobile ? 20 : isLargeMobile ? 24 : 28,
    height: isTinyMobile ? 10 : isSmallMobile || isMediumMobile ? 12 : isLargeMobile ? 14 : 16,
    borderRadius: isTinyMobile ? 5 : isSmallMobile || isMediumMobile ? 6 : isLargeMobile ? 7 : 8,
    justifyContent: "center",
    position: "relative",
  },
  toggleThumb: {
    width: isTinyMobile ? 6 : isSmallMobile || isMediumMobile ? 8 : isLargeMobile ? 10 : 12,
    height: isTinyMobile ? 6 : isSmallMobile || isMediumMobile ? 8 : isLargeMobile ? 10 : 12,
    borderRadius: isTinyMobile ? 3 : isSmallMobile || isMediumMobile ? 4 : isLargeMobile ? 5 : 6,
    position: "absolute",
  },
  subtleInfoButton: {
    padding: isTinyMobile ? 0 : isSmallMobile || isMediumMobile ? 1 : 2,
    marginLeft: isTinyMobile ? 0 : isSmallMobile || isMediumMobile ? 1 : 2,
    borderRadius: 3,
  },
  filterButton: {
    width: 48,
    height: 48,
    borderRadius: 12,
    alignItems: "center",
    justifyContent: "center",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 3,
    elevation: 2,
  },
  modalOverlay: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    padding: 20,
  },
  modalBackdrop: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  },
  infoModal: {
    width: isMobile ? width - 32 : Math.min(width - 64, 400),
    maxHeight: "80%",
    borderRadius: 16,
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.15,
    shadowRadius: 16,
    elevation: 16,
    overflow: "hidden",
  },
  modalHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    padding: 20,
    paddingBottom: 16,
    borderBottomWidth: 1,
  },
  modalHeaderContent: {
    flexDirection: "row",
    alignItems: "center",
    flex: 1,
  },
  modalHeaderIcon: {
    width: 36,
    height: 36,
    borderRadius: 18,
    alignItems: "center",
    justifyContent: "center",
    marginRight: 12,
  },
  modalHeaderText: {
    flex: 1,
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: "700",
    fontFamily: "Inter-Bold",
    marginBottom: 2,
  },
  modalSubtitle: {
    fontSize: 14,
    fontFamily: "Inter-Regular",
  },
  modalCloseButton: {
    width: 32,
    height: 32,
    borderRadius: 16,
    alignItems: "center",
    justifyContent: "center",
  },
  modalContent: {
    padding: 20,
  },
  benefitsSection: {
    gap: 10,
    marginBottom: 24,
  },
  benefitRow: {
    flexDirection: "row",
    alignItems: "center",
    padding: 14,
    borderRadius: 10,
    gap: 12,
  },
  benefitIcon: {
    width: 32,
    height: 32,
    borderRadius: 16,
    alignItems: "center",
    justifyContent: "center",
  },
  benefitContent: {
    flex: 1,
  },
  benefitTitle: {
    fontSize: 14,
    fontWeight: "600",
    fontFamily: "Inter-SemiBold",
    marginBottom: 2,
  },
  benefitDesc: {
    fontSize: 12,
    fontFamily: "Inter-Regular",
  },
  examplesSection: {
    marginBottom: 24,
  },
  sectionTitle: {
    fontSize: 15,
    fontWeight: "600",
    fontFamily: "Inter-SemiBold",
    marginBottom: 10,
  },
  exampleButton: {
    flexDirection: "row",
    alignItems: "center",
    padding: 12,
    borderRadius: 8,
    marginBottom: 8,
    gap: 10,
  },
  exampleText: {
    fontSize: 13,
    fontFamily: "Inter-Regular",
    flex: 1,
  },
  ctaSection: {
    gap: 12,
    alignItems: "center",
  },
  primaryButton: {
    width: "100%",
    borderRadius: 10,
    overflow: "hidden",
  },
  primaryButtonGradient: {
    paddingVertical: 14,
    alignItems: "center",
    justifyContent: "center",
  },
  primaryButtonText: {
    fontSize: 16,
    fontWeight: "600",
    fontFamily: "Inter-SemiBold",
    color: "#FFFFFF",
  },
  secondaryButtonText: {
    fontSize: 14,
    fontFamily: "Inter-Regular",
    textAlign: "center",
    paddingVertical: 8,
  },
});

export default SearchBar;

